import ItemMetrics from './ItemMetrics';
import { GraphicItemUtils } from '../../../../utils/GraphicItemUtils';
import { GraphicItemMasks } from './GraphicItemMasks';
import { fabric } from 'fabric';
import PicabooConstants from '../../../../common/PicabooConstants';
import EXIF from '../../../../utils/exif';

export const PANNING = 'PANNING';
export default class PageRendererHelperBase extends ItemMetrics {
  constructor(
    dim,
    canvas,
    ratioWidth,
    interactive,
    productInfo,
    nodeRender,
    isMobile = false,
  ) {
    super();
    this._interactive = interactive;
    this._dim = dim;
    this._canvas = canvas;
    this._productInfo = productInfo;
    this.ratioWidth = ratioWidth;
    this.nodeRender = nodeRender;
    this.isMobile = isMobile;
  }

  createCanvas() {
    let canvas = fabric.util.createCanvasElement();
    return canvas;
  }

  getDefaultItemOptions() {
    return {
      selectable: this._interactive,
      borderScaleFactor: this.isMobile ? 2 : 0.5,
      hasBorders: true,
      transparentCorners: false,
      lockScalingFlip: true,
      cornerStyle: 'square',
      borderColor: '#799EB9',
      editingBorderColor: '#799EB9',
      cornerColor: '#799EB9',
      cornerStrokeColor: '#799EB9',
      padding: 0,
      cornerSize: 8,
      minHeight: 10,
      minWidth: 10,
      subTargetCheck: true,
    };
  }

  createFabricGroup(pi, isLowRes) {
    return new Promise((resolve) => {
      this.populateFabricGroup(pi, null, isLowRes).then((group) => {
        resolve(group);
      });
    });
  }

  mobileControls(fabricObject) {
    if (this.isMobile) {
      fabricObject.cornerSize = 20;
      fabricObject.cornerStyle = 'circle';
    }
  }

  populateFabricGroup(pi, group, isLowRes = false) {
    return new Promise((resolve) => {
      let groupProps = {
        ...this.getDefaultItemOptions(),
        left: this.x,
        top: this.y,
        width: this.wellWidth,
        height: this.wellHeight,
        angle: this.fixRotation(pi.get('rotation')),
        pageItemVO: pi,
        lockScalingX: false,
        lockScalingY: false,
        lockUniScaling: false,
        lockSkewingX: true,
        lockSkewingY: true,
        minWidth: 10,
        minHeight: 10,
        subTargetCheck: true,
      };

      if (
        !global.isDesigner &&
        (pi.get('type') === PicabooConstants.WELL_TYPES.ALWAYS_ON_TOP ||
          pi.get('type') === PicabooConstants.WELL_TYPES.FIXED_ELEMENT)
      ) {
        groupProps.hasControls = false;
        groupProps.lockMovementX = true;
        groupProps.lockMovementY = true;
        groupProps.selectable = true;
        groupProps.hoverCursor = 'default';
      }

      this.mobileControls(groupProps);

      if (group) {
        let groupItems = this.decompileGroup(group);
        if (groupItems) {
          if (!this.isBorderUnchanged(pi, group)) {
            if (groupItems.border) {
              group.remove(groupItems.border);
            }
            const borderRect = this.getBorderRect(pi);
            group.insertAt(borderRect, 0);
            if (groupItems.corners) {
              const widthDifference =
                groupItems.corners.width - groupItems.border.width;
              const heightDifference =
                groupItems.corners.height - groupItems.border.height;
              groupItems.corners.width = borderRect.width + widthDifference;
              groupItems.corners.height = borderRect.height + heightDifference;
            }
            groupItems = this.decompileGroup(group);
          }

          if (!this.isOpacityUnchanged(pi, group)) {
            groupItems.img.opacity = pi.get('alpha');
          }

          resolve(this.finalizeFabricUpdate(pi, group, groupItems, groupProps));
        }
      } else {
        let image = this.getFabricImage(pi);

        let borderRect = this.getBorderRect(pi);

        const hoverText = new fabric.IText('\uf142', {
          top: -this.wellHeight / 2 + 12,
          left: this.wellWidth / 2 - 26,
          fill: '#FFFFFF',
          originX: 'left',
          originY: 'top',
          fontSize: 20,
          fontFamily: 'FontAwesome',
          selectable: false,
          visible: false,
          picabooType: 'hoverMenuCircle',
          adjust: {
            left: -26,
            top: 12,
          },
        });

        let hoverMenuCircle = new fabric.Circle({
          top: -this.wellHeight / 2 + 8,
          left: this.wellWidth / 2 - 38,
          selectable: false,
          visible: false,
          originX: 'left',
          originY: 'top',
          radius: 14,
          type: 'circle',
          fill: '#DD8132',
          picabooType: 'hoverMenuCircle',
          adjust: {
            left: -38,
            top: 8,
          },
        });

        const HOVER_RADIUS = 28;
        let hoverPanningCircle = new fabric.Circle({
          top: -HOVER_RADIUS,
          left: -HOVER_RADIUS,
          selectable: false,
          visible: false,
          radius: HOVER_RADIUS,
          type: 'circle',
          fill: 'rgba(256,256,256,.3)',
          picabooType: PANNING,
        });

        const hoverPanningIcon = new fabric.IText('\uf047', {
          top: -20,
          left: -20,
          fill: '#222222',
          fontSize: 40,
          fontFamily: 'FontAwesome',
          selectable: false,
          visible: false,
          picabooType: PANNING,
        });
        let groupItems;
        if (isLowRes) {
          const warningIcon = GraphicItemUtils.getWarningIcon(
            this.wellWidth,
            this.wellHeight,
          );
          const warningCircle = GraphicItemUtils.getWarningCircle(
            this.wellWidth,
            this.wellHeight,
          );
          groupItems = this.compileGroup(image, borderRect, [
            hoverMenuCircle,
            hoverText,
            hoverPanningCircle,
            hoverPanningIcon,
            warningCircle,
            warningIcon,
          ]);
        } else {
          groupItems = this.compileGroup(image, borderRect, [
            hoverMenuCircle,
            hoverText,
            hoverPanningCircle,
            hoverPanningIcon,
          ]);
        }
        this.populateShadow(pi, borderRect, image);
        groupProps.picabooType = 'hoverMenu';
        group = new fabric.Group(groupItems, groupProps);
        resolve(group);
      }
    });
  }

  finalizeFabricUpdate(pi, group, groupItems, groupProps) {
    this.populateShadow(pi, groupItems.border, groupItems.img);

    group.set(groupProps);
    group.setObjectsCoords();
    group.setCoords();
    return group;
  }

  populateShadow(
    pi,
    borderRect,
    image,
    isScaled = false,
    respectLegacy = true,
  ) {
    image.set('shadow', new fabric.Shadow({}));
    if (borderRect) {
      borderRect.set('shadow', new fabric.Shadow({}));
    }

    if (
      (pi.has('dropShadow') &&
        pi.get('dropShadow') &&
        pi.get('dropShadow').get('enabled')) ||
      this.hasLegacyShadow(pi)
    ) {
      let shadow =
        respectLegacy && this.hasLegacyShadow(pi)
          ? {
              color: 'rgba(0,0,0,0.6)',
              blur: 0.009 * this.ratioWidth,
              offsetX: 0.008 * this.ratioWidth,
              offsetY: 0.008 * this.ratioWidth,
              opacity: 1,
              affectStroke: false,
            }
          : this.getDropShadow(pi, false, 0, isScaled);
      if (borderRect && this.borderFilter) {
        borderRect.setShadow(shadow);
      } else {
        image.setShadow(shadow);
      }
    }
  }

  hasLegacyShadow(pi) {
    if (pi.get('shadowEnabled')) {
      return true;
    }
    if (pi.get('filters')) {
      for (const filter of pi.get('filters')) {
        if (filter.get('name') === 'DropShadow') {
          return true;
        }
      }
    }
    return false;
  }

  decompileGroup(group) {
    const elements = group.getObjects();
    return {
      border: elements[0],
      img: elements[1],
      controls: elements.slice(2),
    };
  }

  compileGroup(image, borderRect, controls) {
    let ret = [borderRect, image, ...controls];
    return ret;
  }

  getBorderRect(pi) {
    let maskShape = pi.get('maskShape') === 'null' ? null : pi.get('maskShape');

    // Even if image has no border we create one, but the rect dimensions will equal the image (border width==0)
    let borderProps = {
      width: this.wellWidth,
      height: this.wellHeight,
      originX: 'center',
      originY: 'center',
      fill: 'rgba(0,0,0,0)',
      pageItemVO: pi,
      strokeWidth: 0,
      clipTo: null,
    };

    if (this.borderFilter) {
      Object.assign(borderProps, {
        width: this.wellWidth + this.borderFilter.thickness * 2,
        height: this.wellHeight + this.borderFilter.thickness * 2,
      });

      if (maskShape) {
        Object.assign(borderProps, {
          clipTo: function (ctx) {
            GraphicItemMasks.applyMask(
              ctx,
              maskShape,
              borderProps.width,
              borderProps.height,
            );
          },
          fill: GraphicItemUtils.toColor(this.borderFilter.color),
        });
      } else {
        Object.assign(borderProps, {
          strokeWidth: this.borderFilter.thickness,
          fill: GraphicItemUtils.toColor(this.borderFilter.color),
        });
      }
    }

    return new fabric.Rect(borderProps);
  }

  getDropShadow(pi, border = false, borderWidth = 0, isScaled = false) {
    let shadowSettings = pi.get('dropShadow') && pi.get('dropShadow').toJS();
    if (shadowSettings && shadowSettings.enabled) {
      let shadow = {
        color: shadowSettings.color,
        blur:
          (shadowSettings.blur * this.ratioWidth) /
            (isScaled ? this._dim.scale : 1) +
          (border ? borderWidth : 0.0),
        offsetX:
          (shadowSettings.offsetX * this.ratioWidth) /
            (isScaled ? this._dim.scale : 1) +
          (border ? borderWidth : 0.0),
        offsetY:
          (shadowSettings.offsetY * this.ratioWidth) /
            (isScaled ? this._dim.scale : 1) +
          (border ? borderWidth : 0.0),
        opacity: 1,
        affectStroke: false,
      };

      return shadow;
    }
  }

  addNewObject(pi, isLowRes) {
    return new Promise((resolve, reject) => {
      this.loadAndProcessImage(pi)
        .then((processedImage) => {
          this.createFabricGroup(pi, isLowRes).then((group) => {
            resolve({ group: group, processedImage: processedImage });
          });
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  loadAndProcessImage(pi, existingItem) {
    return new Promise((resolve, reject) => {
      this.loadImages(pi)
        .then((response) => {
          this.loadedImages = response.images;
          pi = response.pi;
          this.processImage(pi, existingItem).then((processedImage) => {
            resolve(processedImage);
          });
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getExifData(imageID, src) {
    return new Promise((resolve) => {
      if (src && src.src && !src.src.includes('https')) {
        resolve(1);
        return;
      }

      try {
        EXIF.getData(src, function () {
          resolve(this && this.exifdata ? this.exifdata.Orientation : 1);
        });
      } catch (e) {
        console.log(e, 'Error getting exif data');
        resolve(1);
      }
    });
  }

  processImage(pi, existingItem) {
    return new Promise((resolve) => {
      GraphicItemUtils.imageCache[pi.get('imageID')] = this.loadedImages.image;
      this.loadedImages.image = this.resizeImage(pi, this.loadedImages.image);
      resolve(this.loadedImages.image);
    });
  }

  populateFilters(pi) {
    for (const filter of pi.get('filters')) {
      if (filter.get('name') === 'Border' && filter.get('enabled') != false) {
        const borderValue = filter.get('value');
        const values = borderValue.includes('|')
          ? borderValue.split('|')
          : [borderValue, 1]; // color|width
        const borderColor = values[0];
        const borderThickness = values[1];
        const actualBorderThickness = this.calcActualBorderThickness(
          borderThickness,
          this.ratioWidth,
        );

        this.borderFilter = {
          color: borderColor,
          thickness: actualBorderThickness,
        };
      }
    }
  }

  isObjectUnchanged(pi, existingItem) {
    let unchanged =
      existingItem !== undefined && pi.equals(existingItem.pageItemVO);
    return unchanged;
  }

  areDimensionsUnchanged(pi, existingItem) {
    return (
      existingItem !== undefined &&
      pi.get('width') === existingItem.pageItemVO.get('width') &&
      pi.get('height') === existingItem.pageItemVO.get('height') &&
      pi.get('rotation') === existingItem.pageItemVO.get('rotation') &&
      pi.get('image_height') === existingItem.pageItemVO.get('image_height') &&
      pi.get('image_width') === existingItem.pageItemVO.get('image_width') &&
      pi.get('image_positionX') ===
        existingItem.pageItemVO.get('image_positionX') &&
      pi.get('image_positionY') ===
        existingItem.pageItemVO.get('image_positionY') &&
      pi.get('image_rotation') === existingItem.pageItemVO.get('image_rotation')
    );
  }

  isBorderUnchanged(pi, existingItem) {
    return (
      existingItem != undefined &&
      pi.get('filters') &&
      pi.get('filters').equals(existingItem.pageItemVO.get('filters'))
    );
  }

  isShapeUnchanged(pi, existingItem) {
    let unchanged =
      existingItem !== undefined &&
      pi.get('maskShape') === existingItem.pageItemVO.get('maskShape');
    return unchanged;
  }

  isOpacityUnchanged(pi, existingItem) {
    return (
      existingItem !== undefined &&
      pi.get('alpha') === existingItem.pageItemVO.get('alpha')
    );
  }

  getExistingCanvasObject(pi) {
    let objects = this._canvas.getObjects();

    let existingItem = objects.find(function (canvasItem, index, array) {
      return (
        canvasItem.pageItemVO &&
        canvasItem.pageItemVO.get('id') === pi.get('id')
      );
    });
    return existingItem;
  }

  updateObjectProperties(existingItem, pi, isLowRes = false) {
    return new Promise((resolve) => {
      resolve(this.populateFabricGroup(pi, existingItem, isLowRes));
    });

    //If the graphic item was previously an empty well we just need to remove it and let the item get added normally
    // if(!existingItem.pageItemVO.get('imageID')) {
    //     this.group = null;
    //     this.borderRect = null;
    //     this.img = null;
    //     canvas.remove(existingItem);
    //     existingItem = null;
    //     addNewObject(pi)
    // }
  }

  fixRotation(rotation) {
    let newRotation = parseFloat(rotation);
    if (newRotation < 0) {
      return newRotation + 360;
    }
    return newRotation;
  }

  static getTextSize(width, text) {
    let textSize = 18;
    const c = fabric.util.createCanvasElement();
    const context = c.getContext('2d');

    context.font = textSize + 'px Arial';
    while (context.measureText(text).width > width - 6) {
      --textSize;
      context.font = textSize + 'px Arial';
    }
    return textSize;
  }
}
